import React, {useCallback, useState, useEffect} from 'react';
import {
    videoOrigins, playingComponent, buildVodRecordsUrls
} from './sessionBroadcastUtils';
import { getBroadcastRecord } from '../ApiUtil';
import { debounce } from 'underscore';
import { toAssetsExtUrl } from 'utils/assetsExtUrl';
import classnames from 'classnames';
import { CaptionsList } from '@bizzabo/components/Captions';
import FeatureToggleUtils from 'FeatureToggleUtils';
import PlayButtonWithSessionAccessControl from './PlayButtonWithSessionAccessControl';
import VodPlayerWrapper from './PlayerWrappers/VodPlayerWrapper';
import { loginClickOrigin } from '../../../constants/Enums';
import { normalizeCaptions } from '../../../utils/normalizeCaptionsUtil';
import config from '../../../config/config';

export function getError(error){
    const errorParts = error?.split('---');
    const message = errorParts && errorParts.length > 0 && errorParts[1];
    switch (message){
    case 'SESSION_REGISTRATION':
        return 'This broadcast is only available to attendees who have saved a seat for the session';
    case 'TICKET_TYPE':
        return 'This recording is only available for specific ticket types';
    default:
        return 'This recording is only available for authorized attendees';
    }
}

const BroadcastRecords = ({
    recordingImageUrl,
    eventId,
    isAuthenticated,
    showEmailLogin,
    eventUrls,
    recordingSession,
    setLoginV2OffsetTop,
    siteConf,
    session,
    me,
    sessionType,
}) => {
    const [isRecordingLoading, setRecordingLoading] = useState(false);
    const [sessionAccessError, setSessionAccessError] = useState(null);
    const [shouldLoadHls, setShouldLoadHls] = useState(false);
    const [recordUrls, setRecordUrls] = useState(null);
    const [captions, setCaptions] = useState();
    const { recordingIsReady, recordingPrimaryAssetType, captionsEnabled, turnOnCaptionsForAttendees } = recordingSession;
    const { id: sessionId } = session;
    const playRecord = useCallback(
        async() => {
            try {
                setRecordingLoading(true);
                const response = await getBroadcastRecord(eventId, sessionId);
                const captions = response?.captions;

                const rawRecordUrl = response?.recordingUrl;
                if (!rawRecordUrl) {
                    return;
                }

                const hlsEnabled = response?.availableHls ?? false;
                setShouldLoadHls(hlsEnabled);

                const recordUrls = buildVodRecordsUrls({ virtualData: response, context: playingComponent.BroadcastRecords });

                setRecordUrls(recordUrls);

                const normalizedCaptions = normalizeCaptions(captions);
                setCaptions(normalizedCaptions);
            } catch (error) {
                setSessionAccessError(getError(error));
            } finally {
                setRecordingLoading(false);
            }
        },
        []
    );

    const { broadcastRecording = false } = recordingSession;

    const playProps = {
        ...(
            recordingIsReady &&
            broadcastRecording &&
            { onClick: debounce(playRecord, 1000, true) }
        )
    };

    const isVODAutoplayEnabled = FeatureToggleUtils.isFeatureEnabled('vod-autoplay') && isAuthenticated && !sessionAccessError;

    useEffect(() => {
        if (broadcastRecording && recordingIsReady && isVODAutoplayEnabled) {
            playRecord();
        }
    }, [recordingIsReady, isVODAutoplayEnabled]);

    const sessionAccessProps = {
        isAuthenticated,
        sessionAccessError,
        setSessionAccessError,
        showEmailLogin,
        eventUrls,
        setLoginV2OffsetTop,
        siteConf,
        playProps
    };

    const videoInitProps = {
        recordUrls,
        hlsEnabled: shouldLoadHls,
        streamType: 'on-demand', // Mux player stream type
        userId: me?.id,
        userEmail: me?.primaryEmail,
        sessionId: session?.id,
        sessionTitle: session?.title,
        eventId,
        muxEnvKey: config.muxEnvKey,
        sessionType,
        captionsEnabled,
        turnOnCaptionsForAttendees,
        captionTracksAvailable: captionsEnabled && !!captions?.length,
    };

    return (
        <div className='live-broadcast-container'>
            <div className="broadcast-record">
                <VodPlayerWrapper {...videoInitProps}>
                    {
                        (props) => {
                            const {isPlayerLoading, videoElRef, isPlayerLoaded, videoPlayerCls, PlyrVideoElementWrapper, src, metadata, captionsEnabled, showCaptionsByDefault, muxPlayerProps} = props;
                            const previewImageCls = classnames({'hidden': isPlayerLoaded});
                            const showPlayBtn = !isVODAutoplayEnabled && recordingIsReady &&
                            broadcastRecording &&
                            !isRecordingLoading &&
                            !isPlayerLoading &&
                            !isPlayerLoaded;

                            return (
                            <>
                                {
                                    (isRecordingLoading || isPlayerLoading) &&
                                    <i className="recording-spinner fa fa-spinner fa-spin fa-2x"/>
                                }
                                <PlyrVideoElementWrapper
                                    ref={videoElRef}
                                    className={videoPlayerCls}
                                    style={{display: isPlayerLoaded ? 'block' : 'none', ...(!captionsEnabled ? { '--captions-button': 'none' } : {})}}
                                    playsInline
                                    preload="none"
                                    crossOrigin="anonymous"
                                    src={src}
                                    autoplay='any'
                                    metadata={metadata}
                                    captionsEnabled={captionsEnabled}
                                    showCaptionsByDefault={showCaptionsByDefault}
                                    muxPlayerProps={muxPlayerProps}
                                >
                                    {captionsEnabled && captions?.length ? <CaptionsList captions={captions} type="native" /> : null}
                                </PlyrVideoElementWrapper>
                                {
                                    recordingImageUrl ?
                                        <img src={toAssetsExtUrl(recordingImageUrl)} className={previewImageCls}/> :
                                        <div className={`empty-preview ${previewImageCls}`}></div>
                                }
                                {
                                    showPlayBtn && (
                                        <PlayButtonWithSessionAccessControl
                                            {...sessionAccessProps}
                                            analyticData={{
                                                clickOrigin: loginClickOrigin.session.onDemandPlayBtn,
                                            }}
                                        />
                                    )
                                }
                                {
                                    (
                                        !recordingIsReady ||
                                        (
                                            recordingIsReady &&
                                            !broadcastRecording
                                        )
                                    ) &&
                                    <div className="broadcast-record-footer">
                                        <span>Recording will be ready soon</span>
                                    </div>
                                }
                            </>
                            );
                        }
                    }
                </VodPlayerWrapper>
            </div>
        </div>
    );
};

export default BroadcastRecords;
